import { yupResolver } from '@hookform/resolvers/yup';
import Close from '@mui/icons-material/Close';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Typography,
} from '@mui/material';
import { useState } from 'react';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import { useUpdateUserPointsMutation } from '@/api/user/userApiSlice';
import { useParams } from 'react-router-dom';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import AppNumberTextField from '@/common/components/form-inputs/AppNumberTextField';
interface Props {
	rewardPoints: string | number;
}

const ChangeIntegralModal = (props: Props) => {
	const { rewardPoints } = props;
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [updateUserPoints] = useUpdateUserPointsMutation();
	const { id } = useParams();

	const defaultValues: DefaultValues<any> = {};

	const formSchema = Yup.object().shape({
		rewardPoints: Yup.number()
			.nullable()
			.test(
				'not-zero',
				t('errorMessage.pleaseInputQuantity'),
				(value) => !!value
			),
	});

	const methods = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	const handleClose = () => {
		methods.reset({});
		setOpen(false);
	};

	const watchRewardPoints = methods.watch('rewardPoints');

	const onSubmit: SubmitHandler<any> = async (data) => {
		const response = await updateUserPoints({ ...data, userId: id });
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.changePointsSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			handleClose();
		}
	};

	return (
		<>
			<Button
				endIcon={<CreateOutlinedIcon />}
				onClick={() => {
					setOpen(true);
				}}
				color='secondary'
				sx={{ marginRight: '24px' }}
			>
				{t('button.edit')}
			</Button>
			<Dialog
				open={open}
				onClose={handleClose}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='sm'
				fullWidth
				onKeyDown={(e) => {
					if (e.key === 'Tab') {
						e.stopPropagation();
					}
				}}
			>
				<DialogTitle variant='h5'>{t('user.editIntegral')}</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={handleClose}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<Close />
				</IconButton>
				<DialogContent>
					<FormProvider {...methods}>
						<form onSubmit={methods.handleSubmit(onSubmit)}>
							<Grid container spacing={2} marginTop={0}>
								<Grid item xs={12} display='flex' alignItems='center'>
									<Box
										width='100%'
										display='flex'
										gap={1}
										flexDirection='column'
									>
										<Grid container>
											<Grid item width={'120px'} marginRight='16px'>
												<Typography variant='body2' color='common.neutral40'>
													{t('user.oldIntegral')}
												</Typography>
											</Grid>
											<Grid item xs>
												<Typography
													variant='body1'
													sx={{ wordBreak: 'break-word' }}
												>
													{rewardPoints}
												</Typography>
											</Grid>
										</Grid>
										<Grid container>
											<Grid item width={'120px'} marginRight='16px'>
												<Typography variant='body2' color='common.neutral40'>
													{t('user.newIntegral')}
												</Typography>
											</Grid>
											<Grid item xs>
												<Typography
													variant='body1'
													sx={{ wordBreak: 'break-word' }}
												>
													{Number(watchRewardPoints) + Number(rewardPoints) ||
														0}
												</Typography>
											</Grid>
										</Grid>
									</Box>
									<AppNumberTextField
										control={methods.control}
										name='rewardPoints'
										fieldLabel={t('user.changeIntegral')}
										type='number'
										size='small'
									/>
								</Grid>
							</Grid>
						</form>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button
						type='submit'
						variant='contained'
						onClick={methods.handleSubmit(onSubmit)}
					>
						{t('button.save')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default ChangeIntegralModal;
